<template>
  <div class="aarrr-metrics-container">
    <!-- 컨트롤 패널 섹션 -->
    <div class="control-panel-section">
      <!-- 컨트롤 패널 헤더 (항상 보임) -->
      <div class="control-header">
        <a-row :gutter="16" align="middle" justify="space-between">
          <a-col>
            <span class="control-title">
              <i class="fa fa-sliders"></i>
              분석 설정
              <a-badge v-if="activeControlCount > 0" :count="activeControlCount" :number-style="{ backgroundColor: '#52c41a', fontSize: '12px' }" style="margin-left: 8px;" />
            </span>
          </a-col>
          <a-col>
            <a-button type="text" @click="isControlPanelCollapsed = !isControlPanelCollapsed" class="collapse-button">
              <template #icon>
                <i :class="isControlPanelCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
              </template>
              {{ isControlPanelCollapsed ? '펼치기' : '접기' }}
            </a-button>
          </a-col>
        </a-row>
      </div>

      <!-- 접을 수 있는 컨트롤 패널 콘텐츠 -->
      <div v-show="!isControlPanelCollapsed" class="control-content">
        <a-row :gutter="16" align="middle" class="control-row">
          <a-col :span="10">
            <label class="control-label">
              기준 설정
              <a-tooltip title="AARRR 메트릭 계산의 기준이 되는 전체 대상을 선택합니다">
                <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
              </a-tooltip>
            </label>
            <a-select
              v-model:value="baselineType"
              style="width: 100%"
              @change="handleBaselineChange"
            >
              <a-select-option value="all-targets">
                전체 이벤트 대상자 ({{ allTargetsCount.toLocaleString() }}건)
              </a-select-option>
              <a-select-option value="kakao-certificate">
                카카오톡 인증서 수신 페이지 ({{ kakaoCertificateCount.toLocaleString() }}건)
              </a-select-option>
            </a-select>
          </a-col>
          <a-col :span="14"></a-col>
        </a-row>

        <a-row :gutter="16" align="middle" class="control-row mt-3" justify="end">
          <a-col :span="4">
            <a-button @click="refreshData" :loading="loading" type="primary" style="width: 100%;">
              <template #icon><ReloadOutlined /></template>
              새로고침
            </a-button>
          </a-col>
        </a-row>
      </div>
    </div>

    <!-- AARRR 요약 카드 -->
    <div class="metrics-summary">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="12" :md="8" :lg="4" :xl="4" v-for="(metric, key) in aarrrSummary" :key="key">
          <a-card
            :hoverable="true"
            class="metric-card"
            :class="`metric-card-${key.toLowerCase()}`"
            @click="selectMetric(key)"
          >
            <div class="metric-icon">
              <component :is="getMetricIcon(key)" />
            </div>
            <div class="metric-content">
              <div class="metric-stage">
                {{ getMetricTitle(key) }}
                <a-tooltip
                  :title="getStageDetailTooltip(key)"
                  placement="right"
                >
                  <QuestionCircleOutlined
                    style="font-size: 12px; color: #999; margin-left: 4px;"
                  />
                </a-tooltip>
              </div>
              <a-tooltip
                :title="`이 단계의 대표 활동 수입니다.\n대표 활동: ${getRepresentativeActivityName(key)}`"
                placement="right"
              >
                <div class="metric-value">{{ metric.count.toLocaleString() }}건</div>
              </a-tooltip>
              <a-tooltip
                :title="`전체 활동 ${totalAttendees.toLocaleString()}건 대비 이 단계의 대표 활동 비율입니다.`"
                placement="right"
              >
                <div class="metric-rate">
                  <span v-if="metric.rate !== null">전체 대비 {{ metric.rate }}%</span>
                  <span v-else>-</span>
                </div>
              </a-tooltip>
              <a-tooltip
                :title="`이 단계에 포함된 모든 활동 유형의 개수입니다. 클릭하여 세부 활동을 확인하세요.`"
                placement="right"
              >
                <div class="metric-activities">
                  {{ metric.activities }}개 활동
                </div>
              </a-tooltip>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 대표 활동 퍼널 차트 -->
    <div class="funnel-chart-section">
      <a-card :bordered="false">
        <template #title>
          <span>
            대표 활동 퍼널
            <a-tooltip
              title="각 AARRR 단계의 대표 활동을 기준으로 한 퍼널입니다. 각 단계를 클릭하여 세부 활동을 확인하세요."
              placement="right"
            >
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 8px;" />
            </a-tooltip>
          </span>
        </template>
        <div class="chart-container" v-if="!chartRefreshing">
          <canvas ref="funnelChart"></canvas>
        </div>
        <div class="chart-container" v-else>
          <a-spin tip="차트를 새로고침 중입니다..." />
        </div>
        <div class="conversion-details">
          <a-row :gutter="16">
            <a-col :span="8" v-for="(conversion, idx) in conversionRates" :key="idx">
              <div class="conversion-item">
                <span class="conversion-label">{{ conversion.from }} → {{ conversion.to }}</span>
                <span class="conversion-value">{{ conversion.rate }}%</span>
              </div>
            </a-col>
          </a-row>
        </div>
      </a-card>
    </div>

    <!-- AARRR 상세 테이블 -->
    <div class="metrics-detail">
      <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
        <a-tab-pane key="all" tab="전체">
          <MetricsTable
            :data="allActivities"
            :loading="loading"
            :total-attendees="totalAttendees"
          />
        </a-tab-pane>
        <a-tab-pane
          v-for="(metric, key) in aarrrMetrics"
          :key="key"
          :tab="getMetricTitle(key)"
        >
          <MetricsTable
            :data="metric.activities"
            :loading="loading"
            :metric-type="key"
            :total-attendees="totalAttendees"
          />
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 활동 타입별 시계열 차트 -->
    <div class="timeline-chart-section">
      <a-card :bordered="false">
        <template #title>
          <span>
            활동 추이 분석
            <a-tooltip
              title="각 AARRR 단계별 일일 활동량 추이를 보여줍니다. X축은 날짜, Y축은 활동 건수를 나타냅니다. 각 선은 AARRR 단계를 의미합니다."
              placement="right"
            >
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 8px;" />
            </a-tooltip>
          </span>
        </template>
        <template #extra>
          <a-tag color="blue">획득</a-tag>
          <a-tag color="green">활성화</a-tag>
          <a-tag color="orange">유지</a-tag>
          <a-tag color="purple">수익</a-tag>
          <a-tag color="red">추천</a-tag>
        </template>
        <div class="chart-container" v-if="!chartRefreshing">
          <canvas ref="timelineChart"></canvas>
        </div>
        <div class="chart-container" v-else>
          <a-spin tip="차트를 새로고침 중입니다..." />
        </div>
      </a-card>
    </div>

    <!-- 내부 전환율 상세 드로워 -->
    <a-drawer
      v-model:open="conversionDrawerVisible"
      :title="`${getMetricTitle(selectedMetricForDrawer)} 단계 상세 전환율`"
      :width="600"
      placement="right"
    >
      <div v-if="selectedMetricData">
        <!-- 대표 활동 정보 -->
        <a-card class="representative-card" :bordered="false">
          <template #title>
            <span class="card-title">
              대표 활동
              <a-tooltip title="이 단계를 대표하는 주요 활동입니다">
                <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 8px;" />
              </a-tooltip>
            </span>
          </template>
          <a-descriptions :column="1" size="small">
            <a-descriptions-item label="활동명">
              {{ selectedMetricData.representativeActivity?.displayName || selectedMetricData.representativeActivity?.type || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="활동 수">
              {{ selectedMetricData.representativeActivity?.count.toLocaleString() || 0 }}건
            </a-descriptions-item>
            <a-descriptions-item label="전체 대비">
              {{ selectedMetricData.rate }}%
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 세부 활동 목록 -->
        <a-card class="activities-card" :bordered="false">
          <template #title>
            <span class="card-title">
              세부 활동 ({{ selectedMetricData.activities.length }}개)
              <a-tooltip title="이 단계에 포함된 모든 활동 목록입니다">
                <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 8px;" />
              </a-tooltip>
            </span>
          </template>
          <a-list
            :data-source="selectedMetricData.activities"
            size="small"
          >
            <template #renderItem="{ item, index }">
              <a-list-item>
                <a-list-item-meta>
                  <template #title>
                    <span class="activity-name">
                      {{ index + 1 }}. {{ item.displayName || item.type }}
                    </span>
                  </template>
                  <template #description>
                    <div class="activity-stats">
                      <span class="stat-item">
                        활동 수: {{ item.count.toLocaleString() }}건
                      </span>
                      <span class="stat-item">
                        <PercentageOutlined />
                        {{ totalAttendees > 0 ? ((item.count / totalAttendees) * 100).toFixed(1) : 0 }}%
                      </span>
                    </div>
                  </template>
                </a-list-item-meta>
              </a-list-item>
            </template>
          </a-list>
        </a-card>

        <!-- 내부 전환율 -->
        <a-card
          v-if="selectedMetricData.internalConversions && selectedMetricData.internalConversions.length > 0"
          class="conversions-card"
          :bordered="false"
        >
          <template #title>
            <span class="card-title">
              단계 내 전환율
              <a-tooltip title="이 단계 내에서 활동 간 전환율을 보여줍니다">
                <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 8px;" />
              </a-tooltip>
            </span>
          </template>
          <div class="conversion-flow">
            <div
              v-for="(conversion, idx) in selectedMetricData.internalConversions"
              :key="idx"
              class="conversion-step"
            >
              <div class="flow-item">
                <div class="flow-node from">
                  <div class="node-name">{{ conversion.from }}</div>
                  <div class="node-count">{{ conversion.fromCount.toLocaleString() }}건</div>
                </div>
                <div class="flow-arrow">
                  <ArrowRightOutlined />
                  <span class="conversion-rate">{{ conversion.rate }}%</span>
                </div>
                <div class="flow-node to">
                  <div class="node-name">{{ conversion.to }}</div>
                  <div class="node-count">{{ conversion.toCount.toLocaleString() }}건</div>
                </div>
              </div>
            </div>
          </div>
        </a-card>

        <!-- 내부 전환율이 없는 경우 -->
        <a-empty
          v-else-if="selectedMetricData.activities.length <= 1"
          description="이 단계는 단일 활동으로 구성되어 있습니다"
          :image="null"
        />
      </div>

      <template #footer>
        <a-button @click="conversionDrawerVisible = false">닫기</a-button>
      </template>
    </a-drawer>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import {
  ReloadOutlined,
  QuestionCircleOutlined,
  UserAddOutlined,
  UserOutlined,
  RocketOutlined,
  HeartOutlined,
  DollarOutlined,
  ShareAltOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  PercentageOutlined
} from '@ant-design/icons-vue';
import Chart from 'chart.js/auto';
import MetricsTable from './MetricsTable.vue';

export default {
  name: 'AarrrMetricsAnalysis',
  emits: ['refresh'],
  components: {
    ReloadOutlined,
    QuestionCircleOutlined,
    UserAddOutlined,
    UserOutlined,
    RocketOutlined,
    HeartOutlined,
    DollarOutlined,
    ShareAltOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ArrowRightOutlined,
    PercentageOutlined,
    MetricsTable
  },
  props: {
    eventData: {
      type: Object,
      required: true
    },
    attendeeData: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const loading = ref(false);
    const activeTab = ref('all');
    const selectedMetric = ref(null);
    const funnelChartInstance = ref(null);
    const timelineChartInstance = ref(null);
    const funnelChart = ref(null);
    const timelineChart = ref(null);
    const conversionDrawerVisible = ref(false);
    const selectedMetricForDrawer = ref(null);
    const selectedMetricData = ref(null);
    const chartRefreshing = ref(false);

    // 컨트롤 패널 관련 변수
    const isControlPanelCollapsed = ref(false);

    // 기준 설정 관련 변수
    const baselineType = ref('all-targets'); // 기본값: 전체 이벤트 대상자
    const allTargetsCount = ref(2207); // 전체 이벤트 대상자 (실제로는 이벤트 참여자 전체)
    const kakaoCertificateCount = ref(0); // 카카오톡 인증서 수신 페이지 방문자

    // 활성 필터 수 계산
    const activeControlCount = computed(() => {
      let count = 0;
      if (baselineType.value !== 'all-targets') count++;
      return count;
    });

    // AARRR 메트릭 매핑
    const aarrrMetrics = ref({
      acquisition: { activities: [], count: 0, rate: 0 },
      activation: { activities: [], count: 0, rate: 0 },
      retention: { activities: [], count: 0, rate: 0 },
      revenue: { activities: [], count: 0, rate: 0 },
      referral: { activities: [], count: 0, rate: 0 }
    });

    // 전체 참여자 수 (선택된 기준에 따라 변경)
    const totalAttendees = computed(() => {
      if (baselineType.value === 'kakao-certificate') {
        return kakaoCertificateCount.value;
      }
      // 기본값: 전체 이벤트 대상자
      return allTargetsCount.value;
    });

    // AARRR 요약 데이터
    const aarrrSummary = computed(() => {
      const summary = {};
      const total = totalAttendees.value;

      Object.keys(aarrrMetrics.value).forEach(key => {
        const metric = aarrrMetrics.value[key];
        summary[key] = {
          count: metric.count,
          rate: total > 0 ? ((metric.count / total) * 100).toFixed(1) : null,
          activities: metric.activities.length,
          trend: Math.random() * 20 - 10 // 임시 트렌드 데이터
        };
      });

      return summary;
    });

    // 전환율 계산
    const conversionRates = computed(() => {
      const stages = ['acquisition', 'activation', 'retention', 'referral'];
      const rates = [];

      for (let i = 0; i < stages.length - 1; i++) {
        const fromCount = aarrrMetrics.value[stages[i]].count;
        const toCount = aarrrMetrics.value[stages[i + 1]].count;
        const rate = fromCount > 0 ? ((toCount / fromCount) * 100).toFixed(1) : 0;

        rates.push({
          from: getMetricTitle(stages[i]),
          to: getMetricTitle(stages[i + 1]),
          rate
        });
      }

      return rates;
    });

    // 모든 활동 데이터 (중복 제거 및 정렬)
    const allActivities = computed(() => {
      const activitiesMap = new Map();

      // 모든 활동을 Map에 저장하여 중복 제거
      Object.values(aarrrMetrics.value).forEach(metric => {
        metric.activities.forEach(activity => {
          const key = activity.type; // type을 키로 사용하여 중복 제거
          if (!activitiesMap.has(key) || activitiesMap.get(key).count < activity.count) {
            // 같은 type이 없거나, 있어도 현재 count가 더 크면 업데이트
            activitiesMap.set(key, activity);
          }
        });
      });

      // Map을 배열로 변환하고 count 기준 내림차순 정렬
      return Array.from(activitiesMap.values()).sort((a, b) => b.count - a.count);
    });

    // AARRR 메트릭 매핑 함수
    const mapActivitiesToAARRR = () => {
      if (!props.eventData || !props.attendeeData) {
        console.log('Missing eventData or attendeeData');
        return;
      }

      const eventMetrics = props.eventData.aarrrMetrics || {};

      // Extract activityTypes array from nested structure
      let activityTypes = [];
      if (props.attendeeData.activityTypes) {
        if (Array.isArray(props.attendeeData.activityTypes)) {
          // Direct array
          activityTypes = props.attendeeData.activityTypes;
        } else if (props.attendeeData.activityTypes.activityTypes && Array.isArray(props.attendeeData.activityTypes.activityTypes)) {
          // Nested structure: attendeeData.activityTypes.activityTypes
          activityTypes = props.attendeeData.activityTypes.activityTypes;
        }
      }

      // Update all targets count from the attendee data (전체 이벤트 대상자)
      if (props.attendeeData.activityTypes && props.attendeeData.activityTypes.totalAttendees) {
        allTargetsCount.value = props.attendeeData.activityTypes.totalAttendees;
      } else if (props.attendeeData.totalAttendees) {
        allTargetsCount.value = props.attendeeData.totalAttendees;
      } else if (props.attendeeData.pagination?.total) {
        allTargetsCount.value = props.attendeeData.pagination.total;
      }

      // 카카오톡 인증서 수신 페이지 방문자 수 찾기
      console.log('Looking for Kakao Certificate activity in:', activityTypes);

      // 실제 "카카오톡 인증서 수신 페이지" 활동을 찾기
      const kakaoCertActivity = activityTypes.find(activity => {
        console.log('Checking activity:', activity);
        // acquisition 단계의 첫 번째 활동이 보통 카카오톡 인증서 수신
        return activity.type === 'kakao_certificate_received' ||
               activity.displayName === '카카오톡 인증서 수신 페이지' ||
               activity.type === 'certificate_page_view' ||
               activity.type === 'kakao_cert_page' ||
               (activity.displayName && activity.displayName.includes('카카오톡'))
      });

      if (kakaoCertActivity) {
        kakaoCertificateCount.value = kakaoCertActivity.count || 0;
        console.log('Found Kakao Certificate activity:', kakaoCertActivity);
      } else {
        // acquisition 단계의 대표 활동 찾기
        if (eventMetrics.acquisition && eventMetrics.acquisition.length > 0) {
          const firstAcquisitionType = eventMetrics.acquisition[0].activityType;
          const firstAcquisitionActivity = activityTypes.find(a => a.type === firstAcquisitionType);
          if (firstAcquisitionActivity) {
            kakaoCertificateCount.value = firstAcquisitionActivity.count || 0;
            console.log('Using first acquisition activity as Kakao cert:', firstAcquisitionActivity);
          }
        }
      }

      console.log('EventMetrics:', eventMetrics);
      console.log('AttendeeData:', props.attendeeData);
      console.log('ActivityTypes:', activityTypes);
      console.log('Total Attendees:', totalAttendees.value);
      console.log('Kakao Certificate Count:', kakaoCertificateCount.value);

      // 각 AARRR 단계별로 활동 매핑
      const mapping = {
        acquisition: [],
        activation: [],
        retention: [],
        revenue: [],
        referral: []
      };

      // AARRR 각 단계 처리 (acquisition, activation, retention, revenue, referral)
      ['acquisition', 'activation', 'retention', 'revenue', 'referral'].forEach(stage => {
        const stageActivities = eventMetrics[stage] || [];

        // activities가 배열인지 확인
        if (Array.isArray(stageActivities)) {
          stageActivities.forEach(activity => {
            // activity가 객체이고 activityType 속성을 가지고 있음
            const activityTypeName = activity.activityType;
            const displayName = activity.displayName || activityTypeName;

            // activityTypes 배열에서 해당 타입 찾기
            const foundActivity = activityTypes.find(a => a.type === activityTypeName);

            if (foundActivity) {
              mapping[stage].push({
                type: foundActivity.type,
                count: foundActivity.count,
                lastUsed: foundActivity.lastUsed,
                displayName: activity.displayName || foundActivity.type
              });
            }
          });
        }
      });

      // 매핑된 데이터를 aarrrMetrics에 저장 (대표 지표 방식)
      Object.keys(mapping).forEach(stage => {
        const activities = mapping[stage];

        // 대표 지표 선택: 첫 번째 활동 또는 count가 가장 큰 활동
        let representativeActivity = null;
        let representativeCount = 0;

        if (activities.length > 0) {
          // 첫 번째 활동을 대표로 사용 (eventMetrics에 정의된 순서대로)
          representativeActivity = activities[0];
          representativeCount = representativeActivity.count;
        }

        // 단계 내 세부 전환율 계산
        const internalConversions = [];
        if (activities.length > 1) {
          for (let i = 0; i < activities.length - 1; i++) {
            const fromActivity = activities[i];
            const toActivity = activities[i + 1];
            const conversionRate = fromActivity.count > 0
              ? ((toActivity.count / fromActivity.count) * 100).toFixed(1)
              : 0;

            internalConversions.push({
              from: fromActivity.displayName || fromActivity.type,
              to: toActivity.displayName || toActivity.type,
              fromCount: fromActivity.count,
              toCount: toActivity.count,
              rate: conversionRate
            });
          }
        }

        aarrrMetrics.value[stage] = {
          activities,
          count: representativeCount,
          representativeActivity,
          internalConversions,
          rate: totalAttendees.value > 0 ?
            ((representativeCount / totalAttendees.value) * 100).toFixed(1) : 0
        };
      });
    };

    // 퍼널 차트 그리기
    const drawFunnelChart = async () => {
      await nextTick();

      // 차트 리프레싱 중이면 스킵
      if (chartRefreshing.value) {
        return;
      }

      // 데이터가 없으면 스킵
      if (!props.eventData || !props.attendeeData) {
        console.warn('Chart data not ready');
        return;
      }

      // 기존 차트 인스턴스 안전하게 제거
      if (funnelChartInstance.value) {
        try {
          funnelChartInstance.value.destroy();
          funnelChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying funnel chart:', e);
        }
      }

      // canvas element 확인
      if (!funnelChart.value) {
        console.warn('Funnel chart canvas not ready');
        return;
      }

      try {
        const ctx = funnelChart.value.getContext('2d');
        if (!ctx) {
          console.warn('Unable to get funnel chart context');
          return;
        }

        const stages = ['acquisition', 'activation', 'retention', 'revenue', 'referral'];
        const data = stages.map(stage => aarrrMetrics.value[stage].count);
        const labels = stages.map(stage => `${getMetricTitle(stage)}\n(${aarrrMetrics.value[stage].count.toLocaleString()})`);

        funnelChartInstance.value = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: '활동 수',
            data,
            backgroundColor: [
              'rgba(24, 144, 255, 0.8)',
              'rgba(82, 196, 26, 0.8)',
              'rgba(250, 173, 20, 0.8)',
              'rgba(114, 46, 209, 0.8)',
              'rgba(245, 34, 45, 0.8)'
            ],
            borderColor: [
              '#1890ff',
              '#52c41a',
              '#faad14',
              '#722ed1',
              '#f5222d'
            ],
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = context.raw;
                  const percentage = totalAttendees.value > 0 ?
                    ((value / totalAttendees.value) * 100).toFixed(1) : 0;
                  return `${value.toLocaleString()}건 (${percentage}%)`;
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                callback: (value) => value.toLocaleString()
              }
            }
          }
        }
      });
      } catch (error) {
        console.error('Error creating funnel chart:', error);
      }
    };

    // 타임라인 차트 그리기
    const drawTimelineChart = async () => {
      await nextTick();

      // 차트 리프레싱 중이면 스킵
      if (chartRefreshing.value) {
        return;
      }

      // 데이터가 없으면 스킵
      if (!props.eventData || !props.attendeeData) {
        console.warn('Chart data not ready');
        return;
      }

      // 기존 차트 인스턴스 안전하게 제거
      if (timelineChartInstance.value) {
        try {
          timelineChartInstance.value.destroy();
          timelineChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying timeline chart:', e);
        }
      }

      // canvas element 확인
      if (!timelineChart.value) {
        console.warn('Timeline chart canvas not ready');
        return;
      }

      try {
        const ctx = timelineChart.value.getContext('2d');
        if (!ctx) {
          console.warn('Unable to get timeline chart context');
          return;
        }

        // 최근 7일 날짜 생성
        const today = new Date();
        const labels = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        labels.push(`${date.getMonth() + 1}/${date.getDate()}`);
      }

      // 실제 데이터가 있는 단계만 필터링하여 데이터셋 생성
      const datasets = [];
      const colors = {
        acquisition: '#1890ff',
        activation: '#52c41a',
        retention: '#faad14',
        revenue: '#722ed1',
        referral: '#f5222d'
      };

      Object.keys(aarrrMetrics.value).forEach((stage) => {
        const metric = aarrrMetrics.value[stage];
        // 데이터가 있는 경우만 차트에 추가
        if (metric.count > 0) {
          // 실제 데이터를 기반으로 한 시뮬레이션 (향후 실제 시계열 데이터로 대체)
          // 현재는 대표 활동 수치를 기준으로 ±20% 변동폭으로 표시
          const baseCount = metric.count;
          const data = labels.map((_, index) => {
            if (index === labels.length - 1) {
              // 마지막 날짜(오늘)는 실제 값
              return baseCount;
            }
            // 이전 날짜들은 실제 값의 80~120% 범위로 시뮬레이션
            const variation = 0.8 + Math.random() * 0.4;
            return Math.floor(baseCount * variation);
          });

          datasets.push({
            label: getMetricTitle(stage),
            data,
            borderColor: colors[stage],
            backgroundColor: colors[stage] + '20',
            tension: 0, // 꺾은선 차트
            pointRadius: 4,
            pointHoverRadius: 6
          });
        }
      });

      // 데이터가 없는 경우 빈 차트 표시
      if (datasets.length === 0) {
        timelineChartInstance.value = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: '데이터 없음',
              data: labels.map(() => 0),
              borderColor: '#d9d9d9',
              backgroundColor: 'transparent',
              borderDash: [5, 5],
              pointRadius: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: false
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: '날짜',
                  font: { size: 12, weight: 'normal' }
                },
                grid: { display: false }
              },
              y: {
                beginAtZero: true,
                max: 100,
                title: {
                  display: true,
                  text: '활동 건수',
                  font: { size: 12, weight: 'normal' }
                },
                ticks: {
                  callback: (value) => `${value}건`
                }
              }
            }
          }
        });
        return;
      }

      timelineChartInstance.value = new Chart(ctx, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false // 범례 숨김 (카드 extra에 태그로 표시)
            },
            tooltip: {
              callbacks: {
                title: (context) => {
                  return `날짜: ${context[0].label}`;
                },
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y}건`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: '날짜',
                font: {
                  size: 12,
                  weight: 'normal'
                }
              },
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: '활동 건수',
                font: {
                  size: 12,
                  weight: 'normal'
                }
              },
              ticks: {
                callback: (value) => `${value}건`
              }
            }
          }
        }
      });
      } catch (error) {
        console.error('Error creating timeline chart:', error);
      }
    };

    // 메트릭 툴팁 텍스트
    const getMetricTooltip = (key) => {
      const tooltips = {
        acquisition: '획득: 이벤트를 처음 인지하고 참여한 사용자의 활동입니다. (예: 카카오톡 인증서 수신, 랜딩 페이지 방문)',
        activation: '활성화: 핵심 기능을 처음 사용한 활동입니다. (예: 공유하기, 인증서 상태 확인)',
        retention: '유지: 반복적으로 참여하는 활동입니다. (예: 재공유, 추가 참여)',
        revenue: '수익: 수익과 관련된 활동입니다. (예: 구매, 결제)',
        referral: '추천: 다른 사용자를 유입시키는 활동입니다. (예: 퀴즈 참여, 병원 정보 확인)'
      };
      return tooltips[key] || '';
    };

    // 메트릭 선택
    const selectMetric = (key) => {
      selectedMetric.value = key;
      activeTab.value = key;
      // 드로워 열기
      selectedMetricForDrawer.value = key;
      selectedMetricData.value = aarrrMetrics.value[key];
      conversionDrawerVisible.value = true;
    };

    // 탭 변경 처리
    const handleTabChange = (key) => {
      activeTab.value = key;
    };

    // 기준 변경 처리
    const handleBaselineChange = (value) => {
      console.log('Baseline changed to:', value);
      // 기준 변경 시 메트릭 재계산을 위해 mapActivitiesToAARRR 호출
      mapActivitiesToAARRR();
    };


    // 데이터 새로고침
    const refreshData = async () => {
      loading.value = true;
      chartRefreshing.value = true;

      // 기존 차트 인스턴스 정리
      if (funnelChartInstance.value) {
        try {
          funnelChartInstance.value.destroy();
          funnelChartInstance.value = null;
        } catch (e) {
          console.warn('Error cleaning up funnel chart:', e);
        }
      }

      if (timelineChartInstance.value) {
        try {
          timelineChartInstance.value.destroy();
          timelineChartInstance.value = null;
        } catch (e) {
          console.warn('Error cleaning up timeline chart:', e);
        }
      }

      // 부모 컴포넌트의 fetchEventData 호출
      emit('refresh');

      // API 호출이 완료될 때까지 대기 후 차트 재생성
      setTimeout(async () => {
        // 데이터 매핑
        mapActivitiesToAARRR();

        // 차트 초기화
        await initializeCharts();

        loading.value = false;
      }, 1500);
    };

    // 유틸리티 함수
    const getMetricTitle = (key) => {
      const titles = {
        acquisition: '획득',
        activation: '활성화',
        retention: '유지',
        revenue: '수익',
        referral: '추천'
      };
      return titles[key] || key;
    };

    const getMetricIcon = (key) => {
      const icons = {
        acquisition: 'UserAddOutlined',
        activation: 'RocketOutlined',
        retention: 'HeartOutlined',
        revenue: 'DollarOutlined',
        referral: 'ShareAltOutlined'
      };
      return icons[key] || 'QuestionCircleOutlined';
    };

    // 각 단계의 대표 활동명 가져오기
    const getRepresentativeActivityName = (key) => {
      const metric = aarrrMetrics.value[key];
      if (metric && metric.representativeActivity) {
        return metric.representativeActivity.displayName ||
               metric.representativeActivity.type ||
               '활동 없음';
      }
      return '활동 없음';
    };

    // 각 단계별 상세 툴팁
    const getStageDetailTooltip = (key) => {
      const tooltips = {
        acquisition: '사용자가 이벤트를 처음 인지하고 유입되는 단계입니다. 랜딩 페이지 방문, 인증서 수신 등이 포함됩니다.',
        activation: '사용자가 핵심 기능을 처음 사용하는 단계입니다. 공유하기, 인증서 확인 등이 포함됩니다.',
        retention: '사용자가 반복적으로 참여하는 단계입니다. 재공유, 추가 활동 등이 포함됩니다.',
        revenue: '수익과 관련된 활동 단계입니다. 구매, 결제 등이 포함됩니다.',
        referral: '다른 사용자를 유입시키는 추천 활동 단계입니다. 퀴즈 참여, 병원 정보 확인 등이 포함됩니다.'
      };
      return tooltips[key] || '';
    };

    // 차트 초기화 함수
    const initializeCharts = async () => {
      // 차트 리프레싱 상태 설정
      chartRefreshing.value = true;

      // 기존 차트 정리
      if (funnelChartInstance.value) {
        try {
          funnelChartInstance.value.destroy();
          funnelChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying funnel chart:', e);
        }
      }

      if (timelineChartInstance.value) {
        try {
          timelineChartInstance.value.destroy();
          timelineChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying timeline chart:', e);
        }
      }

      // DOM 업데이트 대기
      await nextTick();

      // 차트 리프레싱 해제
      chartRefreshing.value = false;

      // DOM 재렌더링 대기
      await nextTick();

      // 차트 그리기
      setTimeout(() => {
        if (funnelChart.value && timelineChart.value) {
          drawFunnelChart();
          drawTimelineChart();
        }
      }, 100);
    };

    // 데이터 변경 감지
    watch([() => props.eventData, () => props.attendeeData], async () => {
      mapActivitiesToAARRR();
      await initializeCharts();
    }, { deep: true });

    // 마운트 시 초기화
    onMounted(async () => {
      // 데이터가 있을 때만 차트 초기화
      if (props.eventData && props.attendeeData) {
        mapActivitiesToAARRR();
        // 약간의 지연 후 차트 초기화 (DOM 준비 보장)
        setTimeout(async () => {
          await initializeCharts();
        }, 500);
      }
    });

    // 언마운트 시 차트 정리
    onBeforeUnmount(() => {
      if (funnelChartInstance.value) {
        try {
          funnelChartInstance.value.destroy();
          funnelChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying funnel chart on unmount:', e);
        }
      }

      if (timelineChartInstance.value) {
        try {
          timelineChartInstance.value.destroy();
          timelineChartInstance.value = null;
        } catch (e) {
          console.warn('Error destroying timeline chart on unmount:', e);
        }
      }
    });

    return {
      loading,
      activeTab,
      selectedMetric,
      aarrrMetrics,
      aarrrSummary,
      conversionRates,
      allActivities,
      totalAttendees,
      funnelChart,
      timelineChart,
      chartRefreshing,
      conversionDrawerVisible,
      selectedMetricForDrawer,
      selectedMetricData,
      baselineType,
      allTargetsCount,
      kakaoCertificateCount,
      // 컨트롤 패널 관련
      isControlPanelCollapsed,
      activeControlCount,
      selectMetric,
      handleTabChange,
      handleBaselineChange,
      refreshData,
      getMetricTitle,
      getMetricIcon,
      getMetricTooltip,
      getRepresentativeActivityName,
      getStageDetailTooltip
    };
  }
};
</script>

<style scoped>
.aarrr-metrics-container {
  padding: 0;
}

/* 컨트롤 패널 스타일 */
.control-panel-section {
  background: #fafafa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.control-header {
  padding-bottom: 0;
}

.control-title {
  font-size: 16px;
  font-weight: 600;
  color: #595959;
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-content {
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  margin-top: 16px;
}

.control-row {
  margin-bottom: 0;
}

.control-row.mt-3 {
  margin-top: 16px;
}

.control-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.collapse-button {
  color: #666;
  font-size: 14px;
}

.collapse-button:hover {
  color: #1890ff;
}

.metrics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.metrics-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 기준 라벨과 셀렉트 박스 래퍼 */
.baseline-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.baseline-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  margin: 0;
  line-height: 32px; /* 버튼 높이와 일치시킴 */
}

/* 셀렉트 박스 중앙 정렬 스타일 */
.baseline-select :deep(.ant-select-selector) {
  text-align: center !important;
}

.baseline-select :deep(.ant-select-selection-item) {
  text-align: center !important;
  display: flex !important;
  justify-content: center !important;
}

/* 메트릭 카드 스타일 */
.metrics-summary {
  margin-bottom: 24px;
}

.metric-card {
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 8px;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.12);
}

.metric-card-acquisition {
  border-top: 3px solid #1890ff;
}

.metric-card-activation {
  border-top: 3px solid #52c41a;
}

.metric-card-retention {
  border-top: 3px solid #faad14;
}

.metric-card-revenue {
  border-top: 3px solid #722ed1;
}

.metric-card-referral {
  border-top: 3px solid #f5222d;
}

.metric-icon {
  font-size: 24px;
  color: #999;
  margin-bottom: 12px;
}

.metric-content {
  text-align: left;
}

.metric-stage {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 24px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.metric-rate {
  font-size: 14px;
  color: #1890ff;
  margin: 4px 0;
}

.metric-activities {
  font-size: 12px;
  color: #999;
}

/* 차트 섹션 스타일 */
.funnel-chart-section,
.timeline-chart-section {
  margin-bottom: 24px;
}

.chart-container {
  height: 300px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.conversion-details {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.conversion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 4px;
}

.conversion-label {
  font-size: 13px;
  color: #666;
}

.conversion-value {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
}

/* 상세 테이블 스타일 */
.metrics-detail {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

:deep(.ant-tabs-nav) {
  margin-bottom: 16px;
}

:deep(.ant-tabs-tab) {
  font-weight: 500;
}

:deep(.ant-tabs-tab-active) {
  font-weight: 600;
}

/* 드로워 카드 스타일 */
.representative-card,
.activities-card,
.conversions-card {
  margin-bottom: 16px;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.activity-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.activity-stats {
  display: flex;
  gap: 16px;
  margin-top: 4px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

/* 전환율 플로우 스타일 */
.conversion-flow {
  padding: 16px 0;
}

.conversion-step {
  margin-bottom: 24px;
}

.conversion-step:last-child {
  margin-bottom: 0;
}

.flow-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
}

.flow-node {
  flex: 1;
  padding: 12px;
  background: white;
  border-radius: 6px;
  text-align: center;
}

.flow-node.from {
  border: 1px solid #d9d9d9;
}

.flow-node.to {
  border: 1px solid #1890ff;
}

.node-name {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 4px;
}

.node-count {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
}

.flow-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  color: #999;
}

.conversion-rate {
  font-size: 14px;
  font-weight: 600;
  color: #52c41a;
}

/* 반응형 스타일 */
@media (max-width: 768px) {
  .metrics-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .chart-container {
    height: 250px;
    padding: 10px;
  }

  .conversion-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .flow-item {
    flex-direction: column;
    gap: 12px;
  }

  .flow-arrow {
    transform: rotate(90deg);
  }
}
</style>